import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./LinkRecordActivity.css";
import { Menu } from "@mui/material";
import { selectUser, selectLinkRecords } from "../../redux/features/appData/";
import { LinkRecord, Link, getUnifiedIconPath } from "winslow-core";
import {
  selectSupportedApps,
  setSupportedApps,
} from "../../redux/features/connectedApps/connectedAppsSlice";
import { iconPath, getDomainFromHostname, downloadFile } from "winslow-core";
import { formatDate2 } from "winslow-core";

import { useNavigate } from "react-router-dom";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import { MyTooltip } from "../common/MyTooltip";
import { apiGetSupportedApps } from "../../services/ShareService";

export const ItemTypes = {
  LINK: "link",
};

interface DropResult {
  id: string;
}

type LinkRecordActivityProps = {
  linkRecord: LinkRecord;
  minimum?: boolean;
  showDrag?: boolean;
  showAIMenu?: boolean;
  showName?: boolean;
  allowContextMenu?: boolean;
  clickIsOpen?: boolean;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onSelect?: (linkRecord: LinkRecord, selected: boolean) => void;
  className?: string;
  displayTime?: boolean;
};

type XY = {
  x: number;
  y: number;
};

export const LinkRecordActivity = ({
  linkRecord,
  minimum = false,
  showDrag = false,
  displayTime = true,
  showAIMenu = false,
  allowContextMenu = true,
  clickIsOpen = false,
  showCheckbox = false,
  isSelected = false,
  onSelect,
  className = "",
}: LinkRecordActivityProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const storeUser = useSelector(selectUser);
  const supportedApps = useSelector(selectSupportedApps);
  const linkRecords = useSelector(selectLinkRecords);
  const dropLinkRecordIdsRef = useRef<string[] | undefined>();
  const [localIsSelected, setLocalIsSelected] = useState(isSelected);
  const { reloadAll } = useCoreDataLoader(true);

  const [showRightClickMenu, setShowRightClickMenu] = useState<boolean>(false);
  const [xy, setXY] = useState<XY | null>(null);
  const [menuXY, setMenuXY] = useState<XY | null>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuRef = useRef<any>(null);

  useEffect(() => {
    setLocalIsSelected(isSelected);
  }, [isSelected]);

  useEffect(() => {
    if (menuRef.current) {
      setAnchorEl(menuRef.current);
    }
  }, [menuRef]);

  useEffect(() => {
    if (!supportedApps || supportedApps.length === 0) {
      (async () => {
        const apps = await apiGetSupportedApps();
        if (apps) {
          dispatch(setSupportedApps(apps));
        }
      })();
    }
  }, [supportedApps, dispatch]);

  const handleClick = (linkRecord: LinkRecord) => {
    if (
      linkRecord &&
      linkRecord.source === "FILE" &&
      linkRecord.links &&
      linkRecord.links[0].title
    ) {
      downloadFile(linkRecord.links[0].url, linkRecord.links[0].title);
      return;
    }

    if (!clickIsOpen) {
      handleOpenExpander(linkRecord);
      return;
    }

    if (linkRecord.links && linkRecord.links.length > 0) {
      window.open(linkRecord.links[0].url, "__new");
    }
  };

  const handleOpenExpander = (linkRecord: LinkRecord) => {};

  const handleMouseMove = (event: any) => {
    setXY({ x: event.clientX, y: event.clientY });
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = !localIsSelected;
    setLocalIsSelected(newSelected);
    onSelect?.(linkRecord, newSelected);
  };

  const link: Link | null =
    linkRecord && linkRecord.links && linkRecord.links.length > 0
      ? linkRecord.links[0]
      : null;

  let appLogoPath = "";
  let appId = link ? link.appId : "";

  if (link && link.appId) {
    if (supportedApps && supportedApps.length > 0) {
      appLogoPath = getUnifiedIconPath({ linkRecord }, supportedApps, {
        theme: (storeUser?.theme as "light-mode" | "dark-mode") || "light-mode",
        forceActivity: true,
      });
    } else {
      const theme =
        (storeUser?.theme as "light-mode" | "dark-mode") || "light-mode";
      if (link.appId === "LINK_PDF") {
        appLogoPath = `/icons/${theme}/app_logos/pdf-logo.png`;
      } else if (link.url?.toLowerCase().includes(".pdf")) {
        appLogoPath = `/icons/${theme}/app_logos/pdf-logo.png`;
      } else {
        appLogoPath = `/icons/${theme}/app_logos/other.svg`;
      }
    }
  }

  if (linkRecord.favicon) {
    appLogoPath = linkRecord.favicon;
  }

  const showTime = formatDate2(new Date(linkRecord.createdAt), true);

  const maxLength = minimum ? 60 : 120;

  let title = ("" + (link && link.title)).substring(0, maxLength);
  title += title.length > maxLength - 1 ? "..." : "";

  let parsed = null;
  const parseURL =
    linkRecord.links && linkRecord.links.length > 0
      ? linkRecord.links[0].url
      : "";

  try {
    parsed = new URL(parseURL);
  } catch (err) {
    console.log("[LinkRecordActivity] can't parse url: ", parseURL);
    return null;
  }

  const baseClassName = "link-record-activity";
  const finalClassName = className
    ? `${baseClassName} ${className}`
    : baseClassName;

  return (
    <>
      <div
        className={finalClassName}
        onClick={(e) => {
          if (!showCheckbox) {
            handleClick(linkRecord);
          } else {
            //handleCheckboxClick(e);
          }
        }}
        onMouseMove={handleMouseMove}>
        {showCheckbox && (
          <div className="link-record-activity-checkbox">
            <input
              type="checkbox"
              checked={localIsSelected}
              onChange={(e) => handleCheckboxClick(e as any)}
              aria-label={`Select ${title}`}
            />
          </div>
        )}
        {showDrag && (
          <div className="link-record-activity-drag">
            <img src={iconPath("drag_active.svg", storeUser)} alt="" />
          </div>
        )}
        <div className="link-record-activity-logo">
          <img src={appLogoPath} alt="" />
        </div>
        <div className="link-record-activity-title-and-star">
          <div className="link-record-activity-title">{title}</div>
          {linkRecord.links?.[0] &&
            linkRecord.source === "LINK" &&
            (linkRecord.links[0].appId === "OTHER" ||
              linkRecord.links[0].appId === "LINK_PDF") && (
              <div className="link-record-activity-url-card-url">
                <a
                  href={linkRecord.links[0].url}
                  target="_blank"
                  rel="noreferrer">
                  {linkRecord.links[0].url}
                </a>
              </div>
            )}
          {linkRecord.folderName && (
            <div className="link-record-activity-subfolder">
              <MyTooltip title={<span>{linkRecord.folderName}</span>}>
                <>
                  in {linkRecord.siteName ? `${linkRecord.siteName}>` : ""}
                  {linkRecord.folderName}
                </>
              </MyTooltip>
            </div>
          )}
        </div>

        {!minimum && (
          <div className="link-record-activity-endcap">
            {linkRecord.tags && linkRecord.tags.length > 0 && (
              <div className="link-record-activity-tags"></div>
            )}

            {displayTime && (
              <div className="link-record-activity-time">{showTime}</div>
            )}
          </div>
        )}
      </div>

      {allowContextMenu && (
        <div
          className="link-record-activity-right-click"
          style={{
            left: menuXY ? menuXY.x : 0,
            top: menuXY ? menuXY.y : 0,
            visibility: showRightClickMenu ? "visible" : "hidden",
            display: showRightClickMenu ? "flex" : "none",
          }}
          ref={menuRef}>
          <Menu
            key={"right-click-menu" + linkRecord.id}
            anchorEl={anchorEl}
            open={showRightClickMenu}
            onClose={() => {
              setShowRightClickMenu(false);
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}></Menu>
        </div>
      )}
    </>
  );
};
